import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, CheckCircle2, Loader2, FileText, Briefcase, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRIES = [
  { id: "corporate_finance_legal", label: "Corporate / finance / legal" },
  { id: "trades_healthcare_hospitality", label: "Trades / healthcare / hospitality" },
  { id: "creative_marketing", label: "Creative / marketing" },
  { id: "other", label: "Other" },
];

export default function ResumeReviewIntake() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");
  const [phase, setPhase] = useState("loading");   // loading | form | thanks | error
  const [review, setReview] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  // Intake fields
  const [f, setF] = useState({
    full_name: "",
    target_job_title: "",
    linkedin_url: "",
    industry: "corporate_finance_legal",
    new_roles: "",
    achievements: "",
    qualifications: "",
    job_ad_url: "",
    job_ad_text: "",
  });
  const [busy, setBusy] = useState(false);
  const [uploads, setUploads] = useState({ cv: false, job_ad: false, photo: false });

  useEffect(() => {
    if (!token) { setErrorMsg("Missing intake link. Check your email for the link we sent after purchase."); setPhase("error"); return; }
    axios.get(`${API}/resume-review/intake/${token}`)
      .then(({ data }) => {
        setReview(data.review);
        setUploads(data.review.files || {});
        if (data.review.stage === "approved_sent") { setPhase("thanks"); return; }
        // Pre-fill from purchase record
        setF((s) => ({ ...s, full_name: `${data.review.first_name || ""}`.trim() }));
        setPhase(data.review.stage === "awaiting_intake" ? "form" : "thanks");
      })
      .catch(() => { setErrorMsg("That intake link isn't valid. Contact hello@theditchcollective.co.nz."); setPhase("error"); });
  }, [token]);

  const uploadFile = async (kind, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await axios.post(`${API}/resume-review/intake/${token}/upload/${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploads((u) => ({ ...u, [kind]: true }));
      toast.success(`${kind === "cv" ? "CV" : kind === "job_ad" ? "Job ad" : "Photo"} uploaded.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed."); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!uploads.cv) { toast.error("Please upload your current CV before submitting."); return; }
    if (!f.target_job_title.trim()) { toast.error("Target job title is required."); return; }
    if (!f.job_ad_text.trim() && !f.job_ad_url.trim()) {
      toast.error("Paste the job ad text (or a URL) so we can tailor to the role.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/resume-review/intake/${token}/submit`, f);
      toast.success("Intake received — draft coming your way within 48 hours.");
      setPhase("thanks");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed. Try again.");
    } finally { setBusy(false); }
  };

  if (phase === "loading") return (
    <div className="min-h-screen bg-[#F2F2EF] flex items-center justify-center">
      <Loader2 className="animate-spin text-[#0D2F4D]" size={32} />
    </div>
  );

  if (phase === "error") return (
    <div className="min-h-screen bg-[#F2F2EF] flex items-center justify-center px-5">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-lg">
        <p className="text-[#B91C1C] font-display font-bold text-lg mb-3">Can't load your intake</p>
        <p className="text-sm text-[#6F8FA8]">{errorMsg}</p>
      </div>
    </div>
  );

  if (phase === "thanks") return (
    <div className="min-h-screen bg-[#F2F2EF] flex items-center justify-center px-5">
      <div className="bg-white rounded-2xl p-8 md:p-10 max-w-lg w-full text-center shadow-xl" data-testid="resume-thanks">
        <CheckCircle2 size={40} className="mx-auto text-[#C6D21F] mb-4" />
        <h1 className="font-display text-3xl font-extrabold text-[#0D2F4D]">Intake received</h1>
        <p className="text-[#6F8FA8] mt-3 leading-relaxed">
          Your CV is with our team. You'll get your tailored version within <b className="text-[#0D2F4D]">48 hours</b>, sent to your email.
        </p>
        <p className="text-xs text-[#6F8FA8] mt-6">Questions? hello@theditchcollective.co.nz</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F2F2EF] py-12 px-5">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <span className="text-xs uppercase tracking-widest font-bold text-[#6F8FA8] bg-white px-3 py-1 rounded-full">
            Resume Service · The Ditch Collective
          </span>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold text-[#0D2F4D] mt-4 leading-tight">
            Let's get your CV Australian-ready
          </h1>
          <p className="text-[#6F8FA8] mt-3 leading-relaxed">
            Fill this in, upload your current CV + the job ad, and you'll have your tailored version within <b className="text-[#0D2F4D]">48 hours</b>. Everything you write goes to a human reviewer — nothing sent without our team's polish.
          </p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl p-6 md:p-8 shadow-sm space-y-6" data-testid="resume-intake-form">
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold text-[#0D2F4D] mb-2">Full name</label>
            <input required data-testid="intake-full-name" type="text" value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })}
              className="w-full rounded-xl border border-[#D6DCE3] px-4 py-3 focus:outline-none focus:border-[#0D2F4D]" />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-widest font-bold text-[#0D2F4D] mb-2">
              Target job title <span className="text-[#B91C1C]">*</span> <span className="normal-case text-[10px] text-[#6F8FA8]">(exact — drives everything downstream)</span>
            </label>
            <input required data-testid="intake-job-title" type="text" placeholder="e.g. Senior Marketing Manager"
              value={f.target_job_title} onChange={(e) => setF({ ...f, target_job_title: e.target.value })}
              className="w-full rounded-xl border border-[#D6DCE3] px-4 py-3 focus:outline-none focus:border-[#0D2F4D]" />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-widest font-bold text-[#0D2F4D] mb-2">LinkedIn URL</label>
              <input type="url" data-testid="intake-linkedin" value={f.linkedin_url} onChange={(e) => setF({ ...f, linkedin_url: e.target.value })}
                placeholder="https://linkedin.com/in/…"
                className="w-full rounded-xl border border-[#D6DCE3] px-4 py-3 focus:outline-none focus:border-[#0D2F4D]" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest font-bold text-[#0D2F4D] mb-2">Industry</label>
              <select data-testid="intake-industry" value={f.industry} onChange={(e) => setF({ ...f, industry: e.target.value })}
                className="w-full rounded-xl border border-[#D6DCE3] px-4 py-3 focus:outline-none focus:border-[#0D2F4D] bg-white">
                {INDUSTRIES.map((i) => <option key={i.id} value={i.id}>{i.label}</option>)}
              </select>
            </div>
          </div>

          {/* Uploads */}
          <div className="grid md:grid-cols-3 gap-3">
            <FileTile kind="cv" label="Current CV" icon={<FileText size={16} />} required uploaded={uploads.cv}
              accept=".pdf,.doc,.docx,.txt" onUpload={(f) => uploadFile("cv", f)} />
            <FileTile kind="job_ad" label="Job ad (file)" icon={<Briefcase size={16} />} uploaded={uploads.job_ad}
              accept=".pdf,.doc,.docx,.txt" onUpload={(f) => uploadFile("job_ad", f)} />
            <FileTile kind="photo" label="Photo (optional)" icon={<Upload size={16} />} uploaded={uploads.photo}
              accept="image/*" onUpload={(f) => uploadFile("photo", f)} />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-widest font-bold text-[#0D2F4D] mb-2">Job ad — pasted text or URL <span className="text-[#B91C1C]">*</span></label>
            <textarea rows={4} data-testid="intake-job-ad-text" value={f.job_ad_text} onChange={(e) => setF({ ...f, job_ad_text: e.target.value })}
              placeholder="Paste the full job description here — we pull keywords from it."
              className="w-full rounded-xl border border-[#D6DCE3] px-4 py-3 focus:outline-none focus:border-[#0D2F4D]" />
            <input type="url" data-testid="intake-job-ad-url" value={f.job_ad_url} onChange={(e) => setF({ ...f, job_ad_url: e.target.value })}
              placeholder="…or paste the job listing URL"
              className="mt-2 w-full rounded-xl border border-[#D6DCE3] px-4 py-3 focus:outline-none focus:border-[#0D2F4D] text-sm" />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-widest font-bold text-[#0D2F4D] mb-2">
              New or updated roles since your last CV <span className="normal-case text-[10px] text-[#6F8FA8]">(company, title, dates, key duties)</span>
            </label>
            <textarea rows={4} data-testid="intake-new-roles" value={f.new_roles} onChange={(e) => setF({ ...f, new_roles: e.target.value })}
              className="w-full rounded-xl border border-[#D6DCE3] px-4 py-3 focus:outline-none focus:border-[#0D2F4D]" />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-widest font-bold text-[#0D2F4D] mb-2">
              Significant achievements <span className="normal-case text-[10px] text-[#6F8FA8]">(quantify — "increased X by Y%", "managed team of X", "delivered $Xk project")</span>
            </label>
            <textarea rows={4} data-testid="intake-achievements" value={f.achievements} onChange={(e) => setF({ ...f, achievements: e.target.value })}
              className="w-full rounded-xl border border-[#D6DCE3] px-4 py-3 focus:outline-none focus:border-[#0D2F4D]" />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-widest font-bold text-[#0D2F4D] mb-2">Qualifications / certifications</label>
            <textarea rows={3} data-testid="intake-quals" value={f.qualifications} onChange={(e) => setF({ ...f, qualifications: e.target.value })}
              className="w-full rounded-xl border border-[#D6DCE3] px-4 py-3 focus:outline-none focus:border-[#0D2F4D]" />
          </div>

          <button type="submit" disabled={busy} data-testid="intake-submit"
            className="w-full inline-flex items-center justify-center gap-2 py-4 rounded-full bg-[#0D2F4D] text-white font-display font-bold hover:bg-[#1a4063] disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={18} /> : <>Send to our team <ArrowRight size={16} /></>}
          </button>
        </form>
      </div>
    </div>
  );
}

function FileTile({ kind, label, icon, required, uploaded, accept, onUpload }) {
  const id = `intake-file-${kind}`;
  return (
    <label htmlFor={id} className={`cursor-pointer rounded-xl border-2 border-dashed p-4 flex items-start gap-2 transition-colors ${uploaded ? "border-[#C6D21F] bg-[#F5F7E5]" : "border-[#D6DCE3] hover:border-[#0D2F4D]"}`}
      data-testid={`intake-tile-${kind}`}>
      <span className={uploaded ? "text-[#0D2F4D]" : "text-[#6F8FA8]"}>{uploaded ? <CheckCircle2 size={16} /> : icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold text-[#0D2F4D]">{label}{required && !uploaded ? " *" : ""}</p>
        <p className="text-[10px] text-[#6F8FA8] mt-0.5">{uploaded ? "Uploaded" : "PDF, DOC, DOCX, TXT"}</p>
      </div>
      <input id={id} type="file" accept={accept} className="hidden"
        onChange={(e) => onUpload(e.target.files?.[0])} />
    </label>
  );
}
